/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Container,
  Slider,
  Stack,
} from "@mui/material";
import {
  Call as CallIcon,
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
} from "@mui/icons-material";
import { useWebRTC } from "src/context/chats/WebRTCContext";
import { useSearchParams } from "react-router-dom";
import useCallTone from "src/hooks/useCallTone";

const ActiveCallWindow: React.FC = () => {
  const {
    callState,
    activeCall,
    acceptCall,
    rejectCall,
    terminateCall,
    remoteStream,
    formattedDuration,
    startCall,
    isStandalone,
    isMuted,
    isUploadingRecording,
    toggleMute,
  } = useWebRTC();

  const [volume, setVolume] = useState(1);
  const [isAutoConnecting, setIsAutoConnecting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // 🔔 Play ringtone / ringback tone
  useCallTone(callState);

  const [searchParams] = useSearchParams();
  const audioRef = useRef<HTMLAudioElement>(null);
  const initialized = useRef(false);
  const wasActive = useRef(false);

  // 🔄 Handle Initial Action from URL (start or accept)
  useEffect(() => {
    if (isStandalone) {
      const action = searchParams.get("action");
      const inboxId = searchParams.get("inboxId");
      const callId = searchParams.get("callId");

      // Set auto-connecting state immediately if we have a valid action
      if (!initialized.current && (action === "start" || action === "accept")) {
        setIsAutoConnecting(true);
      }

      // For outgoing calls: Start immediately
      if (!initialized.current && action === "start" && inboxId) {
        setErrorMsg(null);
        startCall(inboxId)
          .catch((err: any) => {
            console.error("Pop-out: startCall failed", err);
            setErrorMsg(
              err.name === "NotAllowedError"
                ? "Microphone access denied. Please enable it in browser settings."
                : err.message || "Failed to connect call. Please try again.",
            );
          })
          .finally(() => setIsAutoConnecting(false));
        initialized.current = true;
      }

      // For incoming calls: Wait for activeCall to be synced, then auto-accept
      if (
        !initialized.current &&
        action === "accept" &&
        callId &&
        activeCall?.callId === callId &&
        callState === "incoming"
      ) {
        console.log(`Auto-accepting call ${callId} in pop-out...`);
        setErrorMsg(null);
        acceptCall(callId)
          .catch((err: any) => {
            console.error("Pop-out: acceptCall failed", err);
            setErrorMsg(
              err.name === "NotAllowedError"
                ? "Microphone access denied. Please enable it in browser settings."
                : err.message || "Failed to connect call. Please try again.",
            );
          })
          .finally(() => setIsAutoConnecting(false));
        initialized.current = true;
      }
    }
  }, [
    isStandalone,
    searchParams,
    startCall,
    acceptCall,
    activeCall,
    callState,
  ]);

  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // 🔄 Track if call was ever active
  useEffect(() => {
    if (callState !== "idle") {
      wasActive.current = true;
    }
  }, [callState]);

  // 🔄 Auto-close window when call ends or fails — but wait for the
  // post-call recording upload to finish first, otherwise the window
  // closes mid-stream and we get a "Request disconnected during file
  // upload stream parsing" error on the backend.
  useEffect(() => {
    // Close if it was active before OR if there is an error message, and now it's idle
    if (
      isStandalone &&
      (wasActive.current || errorMsg) &&
      callState === "idle" &&
      !isUploadingRecording
    ) {
      // If there was an error, wait longer (10s) so agent can read it
      // Otherwise close quickly (2s)
      const delay = errorMsg ? 10000 : 2000;
      const timeout = setTimeout(() => {
        window.close();
      }, delay);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [callState, isStandalone, errorMsg, isUploadingRecording]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #4D1277 0%, #1a1a1a 100%)",
        color: "white",
        p: 3,
        textAlign: "center",
      }}
    >
      <Container maxWidth="xs">
        <Box sx={{ mb: 4, position: "relative" }}>
          <Avatar
            sx={{
              width: 120,
              height: 120,
              margin: "0 auto",
              border: "4px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 0 20px rgba(0,0,0,0.5)",
              animation:
                callState === "ringing" || callState === "incoming"
                  ? "pulse 2s infinite"
                  : "none",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0px rgba(37, 211, 102, 0.4)" },
                "70%": { boxShadow: "0 0 0 20px rgba(37, 211, 102, 0)" },
                "100%": { boxShadow: "0 0 0 0px rgba(37, 211, 102, 0)" },
              },
            }}
            src={activeCall?.customerPicture}
          />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {activeCall?.customerName || activeCall?.from || "WhatsApp User"}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            mb: 4,
            fontFamily: "monospace",
            letterSpacing: 2,
          }}
        >
          {(() => {
            if (errorMsg) return "CONNECTION ERROR";
            if (isAutoConnecting) return "CONNECTING...";
            if (callState === "incoming") return "INCOMING CALL";
            if (callState === "ringing") return "RINGING...";
            if (callState === "idle") {
              // After the call ends, prefer the upload-in-flight message
              // so the agent knows not to close the window. The
              // auto-close timer is gated on isUploadingRecording too, so
              // it won't kick in until this is done.
              if (isUploadingRecording) return "UPLOADING RECORDING...";
              return wasActive.current ? "CALL ENDED" : "CONNECTING...";
            }
            return formattedDuration;
          })()}
        </Typography>

        <Box
          sx={{ display: "flex", justifyContent: "center", gap: 3, mt: "auto" }}
        >
          {errorMsg ? (
            <Box>
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {errorMsg}
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <IconButton
                  onClick={() => {
                    setErrorMsg(null);
                    initialized.current = false;
                    window.location.reload();
                  }}
                  sx={{
                    bgcolor: "white",
                    color: "black",
                    "&:hover": { bgcolor: "#eee" },
                  }}
                >
                  <CallIcon />
                </IconButton>
                <IconButton
                  onClick={() => window.close()}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "white",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                  }}
                >
                  <CallEndIcon />
                </IconButton>
              </Stack>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 1 }}
              >
                <Typography variant="caption">Retry</Typography>
                <Typography variant="caption">Close</Typography>
              </Stack>
            </Box>
          ) : isAutoConnecting ? (
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              Initializing secure connection...
            </Typography>
          ) : callState === "incoming" ? (
            <>
              <IconButton
                onClick={() => acceptCall()}
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: "#25D366",
                  color: "white",
                  "&:hover": { bgcolor: "#128C7E" },
                }}
              >
                <CallIcon sx={{ fontSize: 32 }} />
              </IconButton>
              <IconButton
                onClick={() => rejectCall()}
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: "#FF3B30",
                  color: "white",
                  "&:hover": { bgcolor: "#D70015" },
                }}
              >
                <CallEndIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={terminateCall}
              disabled={callState === "idle"}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#FF3B30",
                color: "white",
                "&:hover": { bgcolor: "#D70015" },
                "&.Mui-disabled": { bgcolor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <CallEndIcon sx={{ fontSize: 32 }} />
            </IconButton>
          )}
        </Box>

        {callState === "active" && (
          <Box sx={{ mt: 6, width: "100%", px: 4 }}>
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              justifyContent="center"
            >
              <Box sx={{ textAlign: "center" }}>
                <IconButton
                  onClick={toggleMute}
                  sx={{
                    bgcolor: isMuted ? "#FF3B30" : "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    "&:hover": {
                      bgcolor: isMuted ? "#D70015" : "rgba(255, 255, 255, 0.2)",
                    },
                    mb: 1,
                  }}
                >
                  {isMuted ? <MicOffIcon /> : <MicIcon />}
                </IconButton>
                <Typography variant="caption" display="block">
                  {isMuted ? "Muted" : "Mute"}
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1, maxWidth: 150 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <VolumeUpIcon sx={{ opacity: 0.6 }} />
                  <Slider
                    size="small"
                    value={volume * 100}
                    onChange={(_, newValue) =>
                      setVolume((newValue as number) / 100)
                    }
                    aria-label="Volume"
                    sx={{
                      color: "#25D366",
                      "& .MuiSlider-thumb": {
                        width: 12,
                        height: 12,
                      },
                    }}
                  />
                </Stack>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Volume
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Container>

      {callState !== "idle" && (
        <Typography
          variant="caption"
          sx={{
            mt: 4,
            opacity: 0.8,
            color: "#ff9800",
            maxWidth: 280,
            textAlign: "center",
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          ⚠️ Closing or refreshing this window <br /> will hang up the call.
        </Typography>
      )}
      {callState === "idle" && isUploadingRecording && (
        <Typography
          variant="caption"
          sx={{
            mt: 4,
            opacity: 0.9,
            color: "#25D366",
            maxWidth: 280,
            textAlign: "center",
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          ⏳ Uploading recording... <br /> Please don&apos;t close the window.
        </Typography>
      )}

      <audio ref={audioRef} autoPlay />
    </Box>
  );
};

export default ActiveCallWindow;
